import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Plus, Cloud, AlertCircle, Loader2, RefreshCw } from 'lucide-react'

interface EventHubConfig {
  id: string
  config_name: string
  namespace: string
  eventhub_name: string
  consumer_group: string
  is_active: boolean
  sync_status: 'connected' | 'disconnected' | 'error'
  last_sync_at?: number
  created_at: number
}

export function EventHubSection() {
  const queryClient = useQueryClient()

  // Fetch configurations
  const { data, isLoading } = useQuery({
    queryKey: ['eventhub-configs'],
    queryFn: async () => {
      const res = await fetch('/api/settings/eventhub')
      if (!res.ok) throw new Error('Failed to fetch Event Hub configurations')
      const data = await res.json()
      return data.configs as EventHubConfig[]
    }
  })

  const configs = data || []
  const [showAddForm, setShowAddForm] = useState(false)
  const [newConfig, setNewConfig] = useState({
    config_name: '',
    namespace: '',
    eventhub_name: '',
    consumer_group: '$Default',
    connection_string: ''
  })

  // Create configuration mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof newConfig) => {
      const res = await fetch('/api/settings/eventhub', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!res.ok) throw new Error('Failed to create configuration')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventhub-configs'] })
      setNewConfig({
        config_name: '',
        namespace: '',
        eventhub_name: '',
        consumer_group: '$Default',
        connection_string: ''
      })
      setShowAddForm(false)
    }
  })

  const handleAddConfig = () => {
    createMutation.mutate(newConfig)
  }

  // Test connection mutation
  const testMutation = useMutation({
    mutationFn: async (configId: string) => {
      const res = await fetch(`/api/settings/eventhub/${configId}/test`, {
        method: 'PATCH'
      })
      if (!res.ok) throw new Error('Connection test failed')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventhub-configs'] })
    }
  })

  const handleTestConnection = (configId: string) => {
    testMutation.mutate(configId)
  }

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: async (configId: string) => {
      const res = await fetch(`/api/settings/eventhub/${configId}/toggle`, {
        method: 'PATCH'
      })
      if (!res.ok) throw new Error('Failed to toggle configuration')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventhub-configs'] })
    }
  })

  const handleToggleActive = (configId: string) => {
    toggleMutation.mutate(configId)
  }

  // Manual sync mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/settings/eventhub/sync', {
        method: 'POST'
      })
      if (!res.ok) throw new Error('Failed to trigger sync')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventhub-configs'] })
    }
  })

  const handleManualSync = () => {
    syncMutation.mutate()
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500">Connected</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      default:
        return <Badge variant="secondary">Disconnected</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Azure Event Hub</h3>
          <p className="text-sm text-muted-foreground">
            Configure Azure Event Hub for real-time telemetry ingestion
          </p>
        </div>
        <div className="flex gap-2">
          {configs.length > 0 && (
            <Button
              variant="outline"
              onClick={handleManualSync}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sync Now
            </Button>
          )}
          <Button onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Configuration
          </Button>
        </div>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add Event Hub Configuration</CardTitle>
            <CardDescription>
              Connect to Azure Event Hub for IoT telemetry data ingestion
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="configName">Configuration Name</Label>
              <Input
                id="configName"
                placeholder="e.g., Production Fleet Telemetry"
                value={newConfig.config_name}
                onChange={(e) => setNewConfig({ ...newConfig, config_name: e.target.value })}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="namespace">Namespace</Label>
                <Input
                  id="namespace"
                  placeholder="e.g., fleetvision-eventhub"
                  value={newConfig.namespace}
                  onChange={(e) => setNewConfig({ ...newConfig, namespace: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="eventhubName">Event Hub Name</Label>
                <Input
                  id="eventhubName"
                  placeholder="e.g., vehicle-telemetry"
                  value={newConfig.eventhub_name}
                  onChange={(e) => setNewConfig({ ...newConfig, eventhub_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="consumerGroup">Consumer Group</Label>
              <Input
                id="consumerGroup"
                placeholder="$Default"
                value={newConfig.consumer_group}
                onChange={(e) => setNewConfig({ ...newConfig, consumer_group: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="connectionString">Connection String</Label>
              <Input
                id="connectionString"
                type="password"
                placeholder="Endpoint=sb://..."
                value={newConfig.connection_string}
                onChange={(e) => setNewConfig({ ...newConfig, connection_string: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Your connection string will be encrypted before storage
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAddConfig}
                disabled={!newConfig.config_name || !newConfig.namespace || !newConfig.eventhub_name || !newConfig.connection_string}
              >
                Save Configuration
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {isLoading && (
          <Card>
            <CardContent className="py-8 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Loading configurations...</p>
            </CardContent>
          </Card>
        )}

        {!isLoading && configs.length === 0 && !showAddForm && (
          <Card>
            <CardContent className="py-8 text-center">
              <Cloud className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mt-4 text-muted-foreground">No Event Hub configurations</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect Azure Event Hub to ingest real-time vehicle telemetry
              </p>
              <Button variant="link" onClick={() => setShowAddForm(true)} className="mt-4">
                Add your first configuration
              </Button>
            </CardContent>
          </Card>
        )}

        {configs.map((config) => (
          <Card key={config.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{config.config_name}</h4>
                    {getStatusBadge(config.sync_status)}
                    {config.is_active && (
                      <Badge variant="outline" className="text-xs">Active</Badge>
                    )}
                  </div>

                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Namespace:</span>
                      <code className="rounded bg-muted px-1.5 py-0.5">
                        {config.namespace}.servicebus.windows.net
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Event Hub:</span>
                      <code className="rounded bg-muted px-1.5 py-0.5">
                        {config.eventhub_name}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Consumer Group:</span>
                      <code className="rounded bg-muted px-1.5 py-0.5">
                        {config.consumer_group}
                      </code>
                    </div>
                  </div>

                  {config.last_sync_at && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Last sync: {formatDate(config.last_sync_at)}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestConnection(config.id)}
                  >
                    Test Connection
                  </Button>
                  <Button
                    variant={config.is_active ? 'secondary' : 'default'}
                    size="sm"
                    onClick={() => handleToggleActive(config.id)}
                  >
                    {config.is_active ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {configs.length > 0 && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Real-time Data Ingestion</p>
                <p className="mt-1 text-muted-foreground">
                  FleetVision workers will consume events from your Event Hub and store telemetry data
                  in D1 database. Make sure your Event Hub has the correct access policies configured.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
